import { useState } from "react";
import { useUserAccounts } from "@trader-dashboard/shared";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { ReportsApi, EOYReportData } from '@/api/ReportsApi';
import { formatCurrency } from '@/utils/formatCurrency';

export const EOYSummaryReportPage = (): React.ReactNode => {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<string>(currentYear.toString());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [reportData, setReportData] = useState<EOYReportData | undefined>(undefined);

  const { selectedAccountId } = useUserAccounts();

  // Generate year options (current year and 5 years back)
  const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i);

  const loadReport = async () => {
    if (!selectedAccountId || !selectedYear) return;

    setLoading(true);
    setError(undefined);

    try {
      const data = await ReportsApi.getEOYSummaryReport(
        selectedAccountId,
        parseInt(selectedYear),
        'Australia/Sydney' // Use broker's timezone
      );
      setReportData(data);
    } catch (err: any) {
      console.error('Error loading EOY report:', err);
      setError(err.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid gap-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">End of Year Summary Report</CardTitle>
          <CardDescription>
            View trading statistics for a specific year
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 items-end">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium">Select Year</label>
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {yearOptions.map(year => (
                    <SelectItem key={year} value={year.toString()}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={loadReport} disabled={loading}>
              {loading ? 'Loading...' : 'Generate Report'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {error && (
        <Card>
          <CardContent className="p-8">
            <div className="text-center text-red-600">Error: {error}</div>
          </CardContent>
        </Card>
      )}

      {reportData && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Year {reportData.year} Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-3 mb-4">
                <div className="rounded-lg border p-4">
                  <div className="text-sm font-medium text-muted-foreground">Total Orders</div>
                  <div className="text-3xl font-bold">{reportData.totalOrders}</div>
                </div>
                <div className="rounded-lg border p-4">
                  <div className="text-sm font-medium text-muted-foreground">Open Orders</div>
                  <div className="text-3xl font-bold">{reportData.openOrders}</div>
                </div>
                <div className="rounded-lg border p-4">
                  <div className="text-sm font-medium text-muted-foreground">Closed Orders</div>
                  <div className="text-3xl font-bold">{reportData.closedOrders}</div>
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-4">
                <div className="rounded-lg border p-4">
                  <div className="text-sm font-medium text-muted-foreground">Gross Profit</div>
                  <div className={`text-2xl font-bold ${reportData.grossProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(reportData.grossProfit)}
                  </div>
                </div>
                <div className="rounded-lg border p-4">
                  <div className="text-sm font-medium text-muted-foreground">Commission</div>
                  <div className={`text-2xl font-bold ${reportData.totalCommission >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(reportData.totalCommission)}
                  </div>
                </div>
                <div className="rounded-lg border p-4">
                  <div className="text-sm font-medium text-muted-foreground">Swap</div>
                  <div className={`text-2xl font-bold ${reportData.totalSwap >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(reportData.totalSwap)}
                  </div>
                </div>
                <div className="rounded-lg border p-4">
                  <div className="text-sm font-medium text-muted-foreground">Net Profit</div>
                  <div className={`text-2xl font-bold ${reportData.totalProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(reportData.totalProfit)}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Monthly Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Month</TableHead>
                    <TableHead className="text-right">Orders</TableHead>
                    <TableHead className="text-right">Open</TableHead>
                    <TableHead className="text-right">Closed</TableHead>
                    <TableHead className="text-right">Gross P/L</TableHead>
                    <TableHead className="text-right">Commission</TableHead>
                    <TableHead className="text-right">Swap</TableHead>
                    <TableHead className="text-right">Net P/L</TableHead>
                    <TableHead className="text-right">Floating</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reportData.byMonth.map((month) => (
                    <TableRow key={month.month}>
                      <TableCell className="font-semibold">{month.month}</TableCell>
                      <TableCell className="text-right">{month.totalOrders}</TableCell>
                      <TableCell className="text-right">{month.openOrders}</TableCell>
                      <TableCell className="text-right">{month.closedOrders}</TableCell>
                      <TableCell className="text-right">
                        <span className={month.grossProfit >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {formatCurrency(month.grossProfit)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={month.totalCommission >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {formatCurrency(month.totalCommission)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={month.totalSwap >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {formatCurrency(month.totalSwap)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={month.totalProfit >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {formatCurrency(month.totalProfit)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={month.openOrdersValue >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {formatCurrency(month.openOrdersValue)}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                  {reportData.byMonth.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center text-muted-foreground py-8">
                        No orders found for {reportData.year}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};
